/**
 * Centralized order detail page configuration.
 *
 * This file provides a simplified, declarative way to configure order detail pages
 * across all platforms. All sections are defined here, and platforms only need to
 * provide their content renderers.
 *
 * Structure:
 * - Order Information (main column): Content varies by platform/specimen type
 * - Order Status (side column, top): Always the same across all platforms
 * - Actions (side column): Platform-specific actions, but same structure
 * - QC History (side column): LDT Customer Success only
 * - Ordering Provider (side column): LDT only
 * - Pathology Lab (side column): LDT only
 */
import { JSX, ReactNode } from 'react';
import { Box } from '@mui/material';
import { LdtWorkflowDefinition } from '@ldt/workflows/types';
import { OrderRepository } from 'src/api/order_repository';
import { OrderStatusSection } from 'src/distribution_platforms/features/components/order_status_section';
import type { WorkflowDefinition } from 'src/distribution_platforms/features/workflow_definition';
import {
  LdtOrderDetailFieldsFragment,
  OrderBaseFieldsFragment,
  OrderStatus,
} from 'src/types';
import { DistributionPlatform } from 'src/types/env';
import { UserRole } from 'src/types/user_roles';
import { WorkflowType } from 'src/types/workflows';
import {
  isClinicAdmin,
  isCustomerSuccess,
  isCustomerSuccessOrSalesUser,
  isOrderingPhysician,
} from 'src/utils';

export interface OrderDetailDisplayer<
  TOrder extends OrderBaseFieldsFragment = OrderBaseFieldsFragment,
> {
  orderRepository: OrderRepository;
  sections: OrderDetailSection<TOrder>[];
  /**
   * Optional platform-specific content to render in the page header (e.g. LDT
   * encounter/accession IDs for CS/Sales users).
   */
  renderHeaderExtra?: (
    order: TOrder,
    context: OrderDetailDisplayContext,
  ) => ReactNode;
}

/**
 * Section identifier for type safety
 */
export enum OrderDetailSectionId {
  Information = 'information',
  Status = 'status',
  BillingHold = 'billingHold',
  Actions = 'actions',
  OrderingProvider = 'orderingProvider',
  PathologyLab = 'pathologyLab',
}

/**
 * Platform-specific content providers
 */
export interface OrderDetailContentProviders<
  TOrder extends OrderBaseFieldsFragment = OrderBaseFieldsFragment,
> {
  /**
   * Render the order information content.
   * This varies by platform and specimen type.
   * For LDT: includes uploaded files below order fields
   * For SAMD: only order fields
   */
  renderInformation: (
    order: TOrder,
    context: OrderDetailDisplayContext,
  ) => JSX.Element;

  /**
   * Render the actions content.
   * This varies by platform (different action buttons).
   */
  renderActions: (
    order: TOrder,
    context: OrderDetailDisplayContext,
  ) => JSX.Element;

  /**
   * Render the billing hold content (LDT Customer Success only)
   */
  renderBillingHold?: (
    order: TOrder,
    context: OrderDetailDisplayContext,
  ) => JSX.Element;

  /**
   * Optional: Render Ordering Provider content (LDT only)
   */
  renderOrderingProvider?: (
    order: TOrder,
    context: OrderDetailDisplayContext,
  ) => JSX.Element;

  /**
   * Optional: Render Pathology Lab content (LDT only)
   */
  renderPathologyLab?: (
    order: TOrder,
    context: OrderDetailDisplayContext,
  ) => JSX.Element;
}

/**
 * Context available to shouldDisplay functions
 */
export interface OrderDetailDisplayContext {
  userRoles: UserRole[];
  workflowType: WorkflowType;
  workflow: WorkflowDefinition;
}

/**
 * Section definition
 */
export interface OrderDetailSection<
  TOrder extends OrderBaseFieldsFragment = OrderBaseFieldsFragment,
> {
  id: OrderDetailSectionId;
  title: string;
  column: 'main' | 'side';
  order: number; // Order within the column
  shouldDisplay: (order: TOrder, context: OrderDetailDisplayContext) => boolean;
  render: (order: TOrder, context: OrderDetailDisplayContext) => JSX.Element;
  collapsible?: boolean; // Whether the section can be collapsed
  defaultExpanded?: boolean; // Whether the section is expanded by default (only applies if collapsible is true)
  showContentAsHeader?: boolean; // Whether the section content should be shown as a header
}

/**
 * Create the complete order detail configuration
 */
export function createOrderDetailConfiguration<
  TOrder extends OrderBaseFieldsFragment,
>(
  platform: DistributionPlatform,
  contentProviders: OrderDetailContentProviders<TOrder>,
): OrderDetailSection<TOrder>[] {
  const sections: OrderDetailSection<TOrder>[] = [
    // Main Column - Order Information
    {
      id: OrderDetailSectionId.Information,
      title: 'Order Information',
      column: 'main',
      order: 1,
      shouldDisplay: () => true,
      render: (order, context) =>
        contentProviders.renderInformation(order, context),
    },

    // Side Column - Order Status (always shown, same for all platforms)
    {
      id: OrderDetailSectionId.Status,
      title: 'Order Status',
      column: 'side',
      order: 1,
      shouldDisplay: () => true,
      render: (order) => (
        <Box sx={{ mt: 1 }}>
          <OrderStatusSection order={order} platform={platform} />
        </Box>
      ),
    },

    // Side Column - Actions (always shown, but content varies by platform)
    {
      id: OrderDetailSectionId.Actions,
      title: 'Actions',
      column: 'side',
      order: 3,
      shouldDisplay: () => true,
      render: (order, context) =>
        contentProviders.renderActions(order, context),
    },
  ];

  // Add LDT-specific sections
  if (platform === DistributionPlatform.Ldt) {
    // Billing Hold - LDT CS only
    if (contentProviders.renderBillingHold) {
      sections.push({
        id: OrderDetailSectionId.BillingHold,
        title: 'Billing Hold',
        column: 'side',
        order: 2,
        shouldDisplay: (order, context) =>
          (order.status &&
            [
              OrderStatus.QualityControlPending,
              OrderStatus.QualityControlApproved,
              OrderStatus.LabProcessing,
              OrderStatus.AiError,
              OrderStatus.AiProcessing,
              OrderStatus.ReportPending,
            ].includes(order.status) &&
            (context.workflow as LdtWorkflowDefinition)
              .showBillingHoldSection &&
            isCustomerSuccess()) ??
          false,
        render: (order, context) =>
          contentProviders.renderBillingHold!(order, context),
        collapsible: false,
        defaultExpanded: true,
        showContentAsHeader: true,
      });
    }

    // Ordering Provider - LDT only
    if (contentProviders.renderOrderingProvider) {
      sections.push({
        id: OrderDetailSectionId.OrderingProvider,
        title: 'Ordering Provider',
        column: 'side',
        order: 5,
        shouldDisplay: (_order, context) =>
          (
            context.workflow as LdtWorkflowDefinition
          ).showOrderingProviderSection(),
        render: (order, context) =>
          contentProviders.renderOrderingProvider!(order, context),
        collapsible: true,
        defaultExpanded: false,
      });
    }

    // Pathology Lab - LDT only
    if (contentProviders.renderPathologyLab) {
      sections.push({
        id: OrderDetailSectionId.PathologyLab,
        title: 'Pathology Lab',
        column: 'side',
        order: 6,
        shouldDisplay: (order, context) => {
          // CS/Sales can always view and manage the pathology lab assignment;
          // ordering physicians and clinic admins can only view it once set.
          const showForDefaultWorkflow =
            order.status !== OrderStatus.Draft &&
            (isCustomerSuccessOrSalesUser() ||
              ((isOrderingPhysician() || isClinicAdmin()) &&
                (order as unknown as LdtOrderDetailFieldsFragment)
                  .pathologyLab != null)) &&
            [
              WorkflowType.CLINIC,
              WorkflowType.DIRECT_UPLOAD_LAB,
              WorkflowType.ANCILLARY_LAB,
            ].includes(context.workflowType);
          // Only CS/Sales can view and manage the pathology lab assignment for Tempus.
          const showForTempus =
            [
              OrderStatus.QualityControlPending,
              OrderStatus.QualityControlApproved,
              OrderStatus.LabProcessing,
            ].includes(order.status) &&
            isCustomerSuccessOrSalesUser() &&
            context.workflowType === WorkflowType.TEMPUS;

          return showForDefaultWorkflow || showForTempus;
        },
        render: (order, context) =>
          contentProviders.renderPathologyLab!(order, context),
        collapsible: true,
        defaultExpanded: false,
      });
    }
  }

  return sections;
}

/**
 * Get sections for a specific column, filtered and sorted
 */
export function getSectionsForColumn<TOrder extends OrderBaseFieldsFragment>(
  sections: OrderDetailSection<TOrder>[],
  order: TOrder,
  column: 'main' | 'side',
  context: OrderDetailDisplayContext,
): OrderDetailSection<TOrder>[] {
  return sections
    .filter((section) => section.column === column)
    .filter((section) => section.shouldDisplay(order, context))
    .sort((a, b) => a.order - b.order);
}
